import React, { FC } from 'react'
import { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, MutationDefinition } from '@reduxjs/toolkit/query'
import IconUpdate from 'assets/pencil-edit.svg?react'
import IconRefresh from 'assets/refresh.svg?react'
import IconTrash from 'assets/trash.svg?react'
import cn from 'classnames'
import { useIsMobile } from 'hooks/media'
import { TContact } from 'modules/contact/types'
import { Button } from 'shared/components/Button'
import Search from 'src/tailwind-components/extends/Search/Search'
import Widget from 'src/tailwind-components/extends/Widget/Widget'

import { TableColumn, TableColumnHead, TableColumnSkeleton } from './components'

import s from './styles.module.less'
import cs from 'src/common.module.less'

type TProps = {
  contact: TContact[] | undefined
  handleOpenModalDelete: (e: React.SyntheticEvent, el: TContact) => void
  handleOpenModalDetail: (el: TContact) => void
  handleSearchContact: (search: string) => void
  handleUpdateContact: (e: React.SyntheticEvent, contact: TContact | undefined) => void
  refetch: () => void
  className?: string
  deleteContact?: MutationTrigger<
    MutationDefinition<string, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, never, string, 'api'>
  >
  handleOpenContactAdd?: () => void
  isLoading?: boolean
  searchQuery?: string
}

const Table: FC<TProps> = ({
  contact,
  isLoading,
  refetch,
  handleOpenModalDetail,
  handleOpenModalDelete,
  handleOpenContactAdd,
  handleUpdateContact,
  handleSearchContact,
  searchQuery
}) => {
  const isMobile = useIsMobile()

  const searchPlaceholder = 'ФИО / телефон'

  const skeleton = [...new Array(10)].map((_, i) => <TableColumnSkeleton key={i} isMobile={isMobile} />)

  return (
    <Widget>
      <div className={cn(cs.positionRelative, 'bg-on-primary')} data-test-id="table">
        <div className={s.headBlock}>
          <div className={s.headBlockDesktop}>
            <div>
              <h2 className={s.title}>Контакты</h2>
            </div>
            <div className={s.buttons}>
              <IconRefresh className={s.refreshBtn} onClick={refetch} size="sm" />
              <Button
                className={s.addBtnDesktop}
                color="primary"
                onClick={handleOpenContactAdd}
                testId="contacts-page--table--add-btn"
                variant="solid"
              >
                Добавить
              </Button>
            </div>
          </div>
          <div className={s.headBlockMobile}>
            <Button className={s.addBtnMobile} color="primary" onClick={handleOpenContactAdd} size="lg" variant="solid">
              Добавить
            </Button>
          </div>
          <Search handleSearch={handleSearchContact} placeholder={searchPlaceholder} searchQuery={searchQuery || ''} />
        </div>
        <div className={s.table} data-name="table" data-test-id="contacts-page--contact-table">
          <TableColumnHead isMobile={isMobile} />
          {/* eslint-disable-next-line */}
          {!isLoading ? (
            contact?.length ? (
              contact.map((el) => (
                <TableColumn
                  key={el.id}
                  isMobile={isMobile}
                  item={el}
                  onClick={() => handleOpenModalDetail(el)}
                  onDeleteButtonClick={(e: React.SyntheticEvent) => handleOpenModalDelete(e, el)}
                  onUpdateButtonClick={(e: React.SyntheticEvent) => handleUpdateContact(e, el)}
                  deleteButton={
                    el.access.delete ? (
                      <IconTrash
                        containerSize="nm"
                        onClick={(e: React.SyntheticEvent) => handleOpenModalDelete(e, el)}
                        size="xs"
                      />
                    ) : (
                      ''
                    )
                  }
                  updateButton={
                    el.access.update ? (
                      <IconUpdate
                        containerSize="nm"
                        onClick={(e: React.SyntheticEvent) => handleUpdateContact(e, el)}
                        size="xxs"
                      />
                    ) : (
                      ''
                    )
                  }
                />
              ))
            ) : (
              <div className={s.notFound}>У вас пока не добавлено ни одного контакта</div>
            )
          ) : (
            skeleton
          )}
        </div>
      </div>
    </Widget>
  )
}

export default Table
